import i18n from '@/i18n';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { type ReactNode, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';
import { toast } from 'sonner';
import { ThemeProvider } from './theme-provider';

/**
 * Map a failed query/mutation to an i18n key so every failure surfaces a
 * toast instead of silently disappearing (or producing an unhandled
 * rejection). Pages with more specific messaging keep their own handlers.
 */
function errorKeyFor(err: unknown): string {
  if (err instanceof Error && (err as { code?: string }).code === 'ERR_NETWORK') {
    return 'errors.network';
  }
  const code = (err as AxiosError<{ error?: { code?: string } }>)?.response?.data?.error?.code;
  switch (code) {
    case 'ERR_AUTH_TOKEN_INVALID':
      return 'errors.auth_token_invalid';
    case 'ERR_RBAC_FORBIDDEN':
    case 'ERR_RBAC_NOT_PROJECT_MEMBER':
      return 'errors.rbac_forbidden';
    case 'ERR_EVENT_NOT_FOUND':
    case 'ERR_CASE_NOT_FOUND':
    case 'ERR_PROJECT_NOT_FOUND':
    case 'ERR_NOT_FOUND':
      return 'errors.not_found';
    default:
      return 'errors.unknown';
  }
}

export function Providers({ children }: { children: ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 1,
            staleTime: 30_000,
            refetchOnWindowFocus: true,
          },
          mutations: {
            retry: 0,
          },
        },
        queryCache: new QueryCache({
          onError: (err, query) => {
            // Some read models treat a miss as a state the page renders itself
            // (common defects: 404 means "no recurrence"), not as a failure
            // worth interrupting the reader with.
            if (query.meta?.suppressErrorToast === true) return;
            toast.error(i18n.t(errorKeyFor(err)));
          },
        }),
        mutationCache: new MutationCache({
          onError: (err) => {
            toast.error(i18n.t(errorKeyFor(err)));
          },
        }),
      }),
  );

  return (
    <BrowserRouter>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>{children}</ThemeProvider>
        </QueryClientProvider>
      </I18nextProvider>
    </BrowserRouter>
  );
}
